/**
 * What a selection on a page says about a question to the consultant (next phase, step 4c, 2026-10-06): a person
 * selects words on a brief, a report, a study, the limits or the action list, and a chip offers to ask about exactly
 * those words (components/copilot/SelectionAsk).
 *
 * The cards of those pages carry the keys of what they show (askAttrs: data-ask-card, data-ask-defect,
 * data-ask-mechanism, data-ask-recommendation, data-ask-condition), so the question names the defect, the mechanism
 * and the recommendation the passage sits in, not only the words. The helpers here are pure, over the few things a
 * node and a selection expose (the browser's own Node, Element and Selection fit), so they run without a browser:
 * cardKeysOf (the keys of the card a selection sits in), selectionTargetOf (whether it is a selection to ask about),
 * and chipPlacement (where the chip goes, never over the selected words where there is room).
 */
import { aboutWithSelection, quoteOf, type CopilotAbout, type SelectionKeys } from "@/lib/copilot/ask";
import type { CopilotAskRequest } from "@/lib/copilot/panel-state";

/** What a page's cards are marked with. */
export const ASK_CARD_ATTR = "data-ask-card";
const KEY_ATTRS = {
  defect: "data-ask-defect",
  mechanism: "data-ask-mechanism",
  recommendation: "data-ask-recommendation",
  condition: "data-ask-condition",
} as const satisfies Record<keyof SelectionKeys, string>;
const KEYS = Object.keys(KEY_ATTRS) as (keyof SelectionKeys)[];
/** The panel marks itself with this, so a selection inside it is never asked about. */
export const PANEL_ATTR = "data-consultant-panel";
/**
 * A one-time secret marks itself with this (an agent key, a claim code), so a selection of it never offers to ask the
 * consultant: the quote would carry it into the conversation, where it is stored (#1624 review, 2026-10-23).
 */
export const PRIVATE_ATTR = "data-copilot-private";
/** The button that opens the panel marks itself with this: collapsing the panel gives it the focus back. */
export const LAUNCHER_ATTR = "data-consultant-launcher";

/** The attributes a card carries: the card's mark, and each key it has (askAttrs). */
export type AskAttrs = { "data-ask-card": "" } & { [K in keyof SelectionKeys as `data-ask-${K}`]?: string };

/**
 * A card's attributes, to spread on its element: `<div {...askAttrs({ defect, mechanism })}>`. Only the keys it has
 * (a blank one is left out), so a card says no more than it knows.
 */
export function askAttrs(keys: { [K in keyof SelectionKeys]?: string | null }): AskAttrs {
  const out: Record<string, string> = { [ASK_CARD_ATTR]: "" };
  for (const k of KEYS) {
    const v = keys[k]?.trim();
    if (v) out[KEY_ATTRS[k]] = v;
  }
  return out as AskAttrs;
}

/** A node as the helpers read it: the browser's Node, and what a test stands in for it. */
export interface NodeLike {
  nodeType: number;
  parentElement: ElementLike | null;
  /**
   * The tree's links and a text's words, where the helpers walk back from a selection's end (textBefore). A stand-in
   * may leave them out; the walk then finds nothing.
   */
  parentNode?: NodeLike | null;
  previousSibling?: NodeLike | null;
  lastChild?: NodeLike | null;
  nodeValue?: string | null;
}

/** An element as the helpers read it. */
export interface ElementLike extends NodeLike {
  tagName: string;
  getAttribute(name: string): string | null;
  /** The elements under it that match (the browser's; a stand-in may leave it out, and then finds none). */
  querySelectorAll?(selectors: string): ArrayLike<ElementLike>;
}

/** A rectangle on the screen (a DOMRect fits). */
export interface RectLike {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/** A range as the helpers read it. */
export interface RangeLike {
  startContainer: NodeLike;
  endContainer: NodeLike;
  startOffset: number;
  endOffset: number;
  getBoundingClientRect(): RectLike;
  /**
   * The deepest node holding both ends, and whether the range runs over a node (the browser's Range has both). The
   * node is typed `never` so the browser's Range, which takes any Node, fits; the helpers pass it an element.
   */
  commonAncestorContainer?: NodeLike;
  intersectsNode?(node: never): boolean;
}

/** A selection as the helpers read it (the browser's Selection fits). */
export interface SelectionLike {
  isCollapsed: boolean;
  rangeCount: number;
  getRangeAt(index: number): RangeLike;
  toString(): string;
}

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
/** How far back the walk for a selection's real end goes before it gives up. */
const WALK_MAX = 5000;

/** The element a node is, or the one it is in (a text node's parent). */
function elementOf(node: NodeLike | null): ElementLike | null {
  if (!node) return null;
  return node.nodeType === ELEMENT_NODE ? (node as ElementLike) : node.parentElement;
}

/** An element and what it sits in, the nearest first. */
function* withAncestors(start: ElementLike | null): Generator<ElementLike> {
  for (let e = start; e; e = e.parentElement) yield e;
}

/**
 * The keys of the card a node sits in: the nearest card's, each key it lacks filled in from the cards around it (a
 * recommendation's row inside a mechanism's card inside a defect's says all three), the nearest first. null when the
 * node is in no card.
 */
function keysAt(node: NodeLike | null): SelectionKeys | null {
  let inCard = false;
  const keys: SelectionKeys = {};
  for (const e of withAncestors(elementOf(node))) {
    if (e.getAttribute(ASK_CARD_ATTR) === null) continue;
    inCard = true;
    for (const k of KEYS) {
      const v = e.getAttribute(KEY_ATTRS[k])?.trim();
      if (v && keys[k] === undefined) keys[k] = v;
    }
  }
  return inCard ? keys : null;
}

/**
 * The keys of the card a selection sits in, from the nodes it starts and ends in. In one card, that card's. In two
 * different cards, only the keys the two share (the same defect, say): the other card's names are in the passage
 * itself, which the consultant reads. With only one end in a card (a drag from a narrative paragraph into a card), or
 * in none, none: the passage is mostly not about the card, and its words still reach the consultant (#1186 review).
 */
export function cardKeysOf(start: NodeLike | null, end: NodeLike | null): SelectionKeys {
  const a = keysAt(start);
  const b = keysAt(end);
  if (!a || !b) return {};
  const shared: SelectionKeys = {};
  for (const k of KEYS) if (a[k] !== undefined && a[k] === b[k]) shared[k] = a[k];
  return shared;
}

const FIELD_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** An element that takes typed text: a field, or a region marked editable. */
function isEditable(e: ElementLike): boolean {
  if (FIELD_TAGS.has(e.tagName.toUpperCase())) return true;
  const editable = e.getAttribute("contenteditable");
  return editable !== null && editable.toLowerCase() !== "false";
}

/**
 * Whether words at this element may be asked about: inside the page's main content, and not inside the panel, a
 * one-time secret (PRIVATE_ATTR), a field or an editable region.
 */
function isAskable(start: ElementLike | null): boolean {
  let inMain = false;
  for (const e of withAncestors(start)) {
    if (e.getAttribute(PANEL_ATTR) !== null || e.getAttribute(PRIVATE_ATTR) !== null || isEditable(e)) return false;
    if (e.tagName.toUpperCase() === "MAIN") inMain = true;
  }
  return inMain;
}

/** The characters a selection holds that are not space, as the question would carry them. */
const MIN_CHARACTERS = 3;

/** A selection to ask about: its words, where it is on the screen, and the keys of the card it sits in. */
export interface SelectionTarget {
  text: string;
  rect: RectLike;
  keys: SelectionKeys;
}

/**
 * The selection, if it is one to ask about: not collapsed, at least three characters that are not space (as the server
 * would be sent them), both ends in the page's main content and none in the panel, a field or an editable region (nor
 * while one has the focus: a browser reports a selection inside a field as one in its parent), and with an area on the
 * screen. null otherwise.
 */
export function selectionTargetOf(sel: SelectionLike | null, active: ElementLike | null): SelectionTarget | null {
  if (!sel || sel.isCollapsed || sel.rangeCount < 1) return null;
  if (active && [...withAncestors(active)].some(isEditable)) return null;
  const text = sel.toString();
  if ([...quoteOf(text).replace(/\s/g, "")].length < MIN_CHARACTERS) return null;
  const range = sel.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  const end = selectionEnd(range);
  if (!isAskable(elementOf(range.startContainer)) || !isAskable(elementOf(end))) return null;
  if (crossesPrivate(range)) return null;
  return { text, rect, keys: cardKeysOf(range.startContainer, end) };
}

/**
 * Whether the range runs over a one-time secret between its ends: a drag that starts above an agent key and ends below
 * it holds the key though neither end is in it (#1624 review). The ends inside one are refused by isAskable.
 */
function crossesPrivate(range: RangeLike): boolean {
  const root = range.commonAncestorContainer;
  if (!root || !range.intersectsNode) return false;
  const marks = elementOf(root)?.querySelectorAll?.(`[${PRIVATE_ATTR}]`);
  if (!marks) return false;
  for (let i = 0; i < marks.length; i++) if (range.intersectsNode(marks[i] as never)) return true;
  return false;
}

/**
 * The last text with words before a node, in the page's order and never inside it: what a selection that ends at the
 * node's very start last holds. null where there is none, or the node gives no way back (a stand-in without links).
 */
function textBefore(node: NodeLike): NodeLike | null {
  let at: NodeLike | null | undefined = node;
  for (let steps = 0; at && steps < WALK_MAX; steps++) {
    if (!at.previousSibling) {
      // Nothing before it in its parent: the parent comes before it, and holds no words of its own.
      at = at.parentNode;
      continue;
    }
    at = at.previousSibling;
    while (at.lastChild) at = at.lastChild;
    if (at.nodeType === TEXT_NODE && (at.nodeValue ?? "").trim() !== "") return at;
  }
  return null;
}

/**
 * Where a selection ends, as its card is read. A selection that ends at the very start of a later node holds none of
 * it: a triple-click on a card's sentence ends at offset 0 of the next block, which may be another card or lie outside
 * the main content (#1186 review). Its end is then the last words before that node (a drag across two cards that stops
 * at a third's start is in the two: the #1186 re-review's LOW), or, where those cannot be read or asked about, where
 * it starts.
 */
function selectionEnd(range: RangeLike): NodeLike {
  if (range.endOffset !== 0 || range.endContainer === range.startContainer) return range.endContainer;
  const before = textBefore(range.endContainer);
  return before && isAskable(elementOf(before)) ? before : range.startContainer;
}

/** A key press as the shortcut reads it (a KeyboardEvent fits). */
export interface KeyLike {
  code: string;
  /** The character the key types, where the layout moves the key (AZERTY's A is where QWERTY's Q is). */
  key?: string;
  altKey: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}

/** The chip's shortcut, as aria-keyshortcuts writes it and as the announcement says it. */
export const ASK_SHORTCUT = "Alt+Shift+A";

/**
 * The chip's shortcut, for a selection made from the keyboard (#1186 review LOW 3): Alt+Shift+A, with nothing else
 * held. Read by the key's place (code), so a Mac's Option+Shift+A, which types Å, asks too; and by the letter it types,
 * so a layout that moves the key (AZERTY) asks on the key labelled A (#1195 review LOW 2).
 */
export function isAskShortcut(e: KeyLike): boolean {
  if (!e.altKey || !e.shiftKey || e.ctrlKey || e.metaKey) return false;
  return e.code === "KeyA" || e.key === "A" || e.key === "a";
}

/** The keys whose key-up ends no keyboard selection: a modifier released alone, a menu key, Escape. */
const NO_CHIP_KEY_UPS = new Set(["Shift", "Control", "Alt", "AltGraph", "Meta", "OS", "F10", "ContextMenu", "Escape"]);

/**
 * Whether a key-up shows no chip (#1200 review): Shift+F10 opens the context menu on F10's key-down, and F10 and Shift
 * come up after it in either order, each with Shift no longer held; a modifier released alone, a menu key and Escape
 * end no selection (a Shift+Arrow selection shows its chip on the arrow's key-up, and on selectionchange).
 */
export function keyUpShowsNoChip(e: { key: string }): boolean {
  return NO_CHIP_KEY_UPS.has(e.key);
}

/**
 * A press that may start a drag: the primary button (a touch and a pen report it too). A right-click on a Mac opens a
 * menu and sends no pointer-up, so taking it for a drag would keep the chip away until the next click (#1186 review).
 */
export function isPrimaryPress(e: { button: number }): boolean {
  return e.button === 0;
}

/**
 * Whether the focus is free to take: nowhere, on the page's body (where the browser leaves it when what had it hides),
 * or inside the panel. Anywhere else the person moved it themselves, and the panel leaves it there (#1186 review LOW 3:
 * a composer that shows late, and the focus on collapse).
 */
export function focusIsFree(active: ElementLike | null): boolean {
  if (!active || active.tagName.toUpperCase() === "BODY") return true;
  for (const e of withAncestors(active)) if (e.getAttribute(PANEL_ATTR) !== null) return true;
  return false;
}

/**
 * Whether a drafted question may take the focus: it is still on what had it when the question came (a card's Ask is
 * a link, which Chrome, Edge and Firefox on Windows focus on a click), or it is free (focusIsFree). Where the person
 * moved it on while the threads loaded, it stays theirs (#1195 review MEDIUM).
 */
export function focusStillOurs(active: ElementLike | null, opener: ElementLike | null): boolean {
  return (active !== null && active === opener) || focusIsFree(active);
}

/** The chip's size, in pixels: what its placement reckons with (it measures a little less or more by its font). */
export const CHIP_SIZE = { width: 176, height: 32 } as const;
const GAP = 6;
const MARGIN = 8;
const EDGE = 4;

/** The part of the screen the chip may sit in: the page's main content, less the panel. */
export interface Area {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface Placement {
  top: number;
  left: number;
  /** Above or below the selection, or over the top of the area where it leaves room for neither. */
  side: "above" | "below" | "over";
}

/**
 * Where the chip goes: just above the selection, or just below it where there is no room above (or where the person
 * touches the screen, whose own menu is above: prefer "below"), centred on the part of the selection in view and kept
 * inside the area. It sits over the words only where the selection leaves room for neither side.
 */
export function chipPlacement(
  rect: RectLike,
  area: Area,
  opts: { prefer?: "above" | "below"; size?: { width: number; height: number } } = {},
): Placement {
  const { width, height } = opts.size ?? CHIP_SIZE;
  const aboveTop = rect.top - GAP - height;
  const belowTop = rect.bottom + GAP;
  const fitsAbove = aboveTop >= area.top + EDGE;
  const fitsBelow = belowTop + height <= area.bottom - EDGE;

  const above = opts.prefer === "below" ? !fitsBelow && fitsAbove : fitsAbove;
  let side: Placement["side"];
  let top: number;
  if (above) {
    side = "above";
    top = aboveTop;
  } else if (fitsBelow) {
    side = "below";
    top = belowTop;
  } else {
    side = "over";
    top = area.top + EDGE;
  }

  const seen = { left: Math.max(rect.left, area.left), right: Math.min(rect.right, area.right) };
  const centre = seen.left <= seen.right ? (seen.left + seen.right) / 2 : (rect.left + rect.right) / 2;
  const min = area.left + MARGIN;
  const max = area.right - MARGIN - width;
  const left = Math.max(min, Math.min(max, centre - width / 2));
  return { top: Math.round(top), left: Math.round(left), side };
}

/** The question a selection drafts: the person edits it, or writes their own, and sends it. */
export const SELECTION_QUESTION = "What does this mean for us, and what should we do about it?";

/**
 * What the chip asks the panel to open with: the default question, and the page's context with the passage and the card's
 * keys added (aboutWithSelection). The chip keeps the page's label.
 */
export function selectionRequest(page: CopilotAbout, target: Pick<SelectionTarget, "text" | "keys">): CopilotAskRequest {
  return { draft: SELECTION_QUESTION, about: aboutWithSelection(page, target.text, target.keys) };
}
